import { companySetup } from 'Api/companySetup';
import { directDepositApi } from 'Api/directDeposit';
import { Col, Row } from 'antd';
import { Loader } from 'components/Global';
import Buttons from 'components/Global/Buttons';
import InputField from 'components/Global/InputField';
import InputFieldWithZero from 'components/Global/InputFieldWithZero';
import SelectDropdown from 'components/Global/SelectDropdown';
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
	BANK_NAME_OPTIONS,
	BENEFICIARY_CODE_OPTIONS,
	hasFormError,
	invalidText,
	toastText,
	validateFormData,
} from 'utils/utils';
import styles from './index.module.scss';

type DirectDepositProps = {
	onCancel: () => void;
	trueVerifiedAccount: () => void;
	falseVerifiedAccount: () => void;
	isDetailsLoading: boolean;
	paymentDetails: any;
	isUsd: boolean;
	apiCurrencyCode: any;
	setApiCurrencyCode: any;
	isStanbic: boolean;
	setIsStanbic: any;
	payrollDetails: any;
	setPayrollDetails: any;
};

const INITIAL_FORM_ERROR = {
	accountHolderName: false,
	accountNumber: false,
	bankType: false,
	sortCode: false,
	beneficiaryBankCode: false,
	beneficiaryAddr1: false,
	beneficiaryAddr2: false,
	beneficiaryAddr3: false,
};

const ACCOUNT_HOLDER_NAME_REGEX = /^[a-zA-Z ]+$/;
const ADDRESS_REGEX = /^[a-zA-Z0-9 ]+$/;

const DirectDeposit = ({
	onCancel,
	falseVerifiedAccount,
	trueVerifiedAccount,
	paymentDetails,
	apiCurrencyCode,
	setApiCurrencyCode,
	isUsd,
	isStanbic,
	setIsStanbic,
	isDetailsLoading,
	payrollDetails,
	setPayrollDetails,
}: DirectDepositProps) => {
	const [isLoading, setIsLoading] = useState(false);
	const [isVerifyLoading, setIsVerifyLoading] = useState(false);
	const [searchParams] = useSearchParams();
	const [directDepositDetails, setDirectDepositDetails] =
		useState<any>(paymentDetails);
	const [formError, setFormError] = useState<any>(INITIAL_FORM_ERROR);
	const [sortCodeDisabled, setSortCodeDisabled] = useState(false);

	// Memoized options with default selection option
	const beneficiaryCodeDropdownOptions = useMemo(
		() => [
			{ label: 'Select Beneficiary Bank Code', value: '' },
			...BENEFICIARY_CODE_OPTIONS,
		],
		[]
	);

	// Optimized onChange handler with useCallback
	const OnChange = useCallback(
		(value: string | number | null, key: string) => {
			setDirectDepositDetails((prev: any) => ({
				...prev,
				[key]: value,
			}));

			const checkFormError = validateFormData(
				{ [key]: value },
				{ ...formError }
			);
			setFormError(checkFormError);
		},
		[formError]
	);

	// Optimized handleChange with useCallback
	const handleChange = useCallback(
		(
			value: string | number | null,
			name: string,
			required: boolean,
			regex?: RegExp | null
		) => {
			if (typeof value === 'number' && value <= 0) {
				return;
			}

			if (name === 'bankType') {
				const selectedBank = BANK_NAME_OPTIONS.find(
					(bank) => bank.value === value
				);

				if (value === 'STANBIC_BANK') {
					setIsStanbic(true);
					setSortCodeDisabled(false);
					OnChange('', 'sortCode');
				} else if (value === 'FIRST_CAPITAL_BANK') {
					setIsStanbic(false);
					OnChange('280000', 'sortCode');
				} else {
					setIsStanbic(false);
					setSortCodeDisabled(false); // Auto-fill sort code for other banks
					OnChange(selectedBank?.sortCode || '', 'sortCode');
				}

				setApiCurrencyCode(isUsd ? 'USD' : 'ZMW');
				setDirectDepositDetails((prev: any) => ({
					...prev,
					isVerified: false,
					accountNumber: '',
					sortCode: selectedBank?.sortCode || '',
					beneficiaryBankCode: selectedBank?.beneficiaryCode || '',
				}));
				falseVerifiedAccount();
			}

			if (name === 'accountNumber') {
				if (isStanbic) {
					// Stanbic Bank - auto verify
					setDirectDepositDetails((prev: any) => ({
						...prev,
						isVerified: true,
					}));
					trueVerifiedAccount();
				} else if (
					directDepositDetails.bankType === 'FIRST_CAPITAL_BANK'
				) {
					// First Capital Bank - requires manual verification
					setDirectDepositDetails((prev: any) => ({
						...prev,
						isVerified: false,
					}));
					falseVerifiedAccount();
				} else {
					// Other banks - auto verify
					setDirectDepositDetails((prev: any) => ({
						...prev,
						isVerified: true,
					}));
					trueVerifiedAccount();
				}
			}

			OnChange(value, name);
		},
		[
			OnChange,
			isUsd,
			isStanbic,
			directDepositDetails.bankType,
			setApiCurrencyCode,
			setIsStanbic,
			falseVerifiedAccount,
			trueVerifiedAccount,
		]
	);

	// Optimized submit handler
	const handleSubmit = useCallback(async () => {
		const accountHolderName = ACCOUNT_HOLDER_NAME_REGEX.test(
			directDepositDetails.accountHolderName
		);

		if (!accountHolderName) {
			toastText(
				'Account Holder Name must contain letters and spaces',
				'error'
			);
			return;
		}

		if (
			payrollDetails.isPayrollRun &&
			payrollDetails.previousOption === 'CASH'
		) {
			toastText(
				`Direct deposit changes cannot be saved - payroll status: ${payrollDetails.payrollStatus}`,
				'error'
			);
			return;
		}

		// Only check verification for First Capital Bank
		if (directDepositDetails.bankType === 'FIRST_CAPITAL_BANK') {
			if (directDepositDetails.isVerified === false) {
				toastText('Account verification failed', 'error');
				return;
			}
		} else if (!isStanbic) {
			// For other banks (except Stanbic), auto-verify
			setDirectDepositDetails((prev: any) => ({
				...prev,
				isVerified: true,
			}));
			trueVerifiedAccount();
		} else {
			// For Stanbic
			setDirectDepositDetails((prev: any) => ({
				...prev,
				isVerified: true,
			}));
			trueVerifiedAccount();
		}

		setDirectDepositDetails((prev: any) => ({
			...prev,
			isVerified: true,
		}));

		let checkFormError = validateFormData(
			{ accountHolderName: !accountHolderName, ...directDepositDetails },
			{ ...formError }
		);
		setFormError(checkFormError);

		if (hasFormError(checkFormError)) {
			return;
		}

		const data = {
			employeeId: searchParams.get('employeeId'),
			bankType: directDepositDetails.bankType,
			accountNumber: directDepositDetails.accountNumber,
			sortCode: directDepositDetails.sortCode,
			accountHolderName: directDepositDetails.accountHolderName,
			isVerified: directDepositDetails.isVerified,
			beneficiaryAddr1: directDepositDetails.beneficiaryAddr1,
			beneficiaryAddr2: directDepositDetails.beneficiaryAddr2,
			beneficiaryAddr3: directDepositDetails.beneficiaryAddr3,
			beneficiaryBankCode: directDepositDetails.beneficiaryBankCode,
		};

		setIsLoading(true);

		try {
			await directDepositApi.createDirectDeposit(data);
			toastText('Direct Deposit has been saved successfully', 'success');

			setPayrollDetails((prev: any) => ({
				...prev,
				previousOption: 'DIRECT_DEPOSIT',
			}));
		} catch (err: any) {
			const message =
				err.response?.data?.message ||
				'Something went wrong in creating Direct Deposit Request';
			toastText(message, 'error');
		} finally {
			setIsLoading(false);
		}
	}, [
		directDepositDetails,
		payrollDetails,
		isStanbic,
		formError,
		searchParams,
		setPayrollDetails,
		trueVerifiedAccount,
	]);

	// Optimized verify submit handler
	const handleVerifySubmit = useCallback(async () => {
		const currencyCode = isUsd ? 'USD' : 'ZMW';

		setIsVerifyLoading(true);
		try {
			const response = await companySetup.verifyAccountApi({
				accountId: directDepositDetails.accountNumber,
			});

			if (response?.data?.data) {
				const apiData = response.data.data;

				setApiCurrencyCode(apiData.accountCurrencyCode);

				// if (
				// 	directDepositDetails.bankType === 'FIRST_CAPITAL_BANK' &&
				// 	currencyCode !== apiData.accountCurrencyCode
				// ) {
				// 	toastText(
				// 		"Account and Employee's currency is different",
				// 		'error'
				// 	);
				// 	setDirectDepositDetails((prev: any) => ({
				// 		...prev,
				// 		isVerified: false,
				// 	}));
				// } 
					trueVerifiedAccount();
					toastText('Account verified successfully', 'success');
					setDirectDepositDetails((prev: any) => ({
						...prev,
						isVerified: true,
					}));
			}
		} catch (err: any) {
			falseVerifiedAccount();
			setDirectDepositDetails((prev: any) => ({
				...prev,
				isVerified: false,
			}));
			toastText('Account verification failed', 'error');
		} finally {
			setIsVerifyLoading(false);
		}
	}, [
		isUsd,
		directDepositDetails.accountNumber,
		directDepositDetails.bankType,
		setApiCurrencyCode,
		trueVerifiedAccount,
		falseVerifiedAccount,
	]);

	// Memoized button configurations
	const verifyButton = useMemo(
		() => ({
			text: 'Verify',
			isLoading: isVerifyLoading,
			className: 'primary-button',
			fontSize: '1.8rem',
			minWidth: '12rem',
			minHeight: '4rem',
			isSubmit: true,
			onclick: handleVerifySubmit,
		}),
		[isVerifyLoading, handleVerifySubmit]
	);

	const myButtons = useMemo(
		() => [
			{
				text: 'Cancel',
				isLoading: false,
				className: 'secondary-button',
				fontSize: '1.8rem',
				minWidth: '12rem',
				minHeight: '4rem',
				isSubmit: false,
				disabled: isLoading,
				onclick: onCancel,
			},
			{
				text: 'Save',
				isLoading: isLoading,
				className: 'primary-button',
				fontSize: '1.8rem',
				minWidth: '12rem',
				minHeight: '4rem',
				isSubmit: true,
				onclick: handleSubmit,
				disabled: !directDepositDetails.isVerified,
			},
		],
		[isLoading, handleSubmit, onCancel, directDepositDetails.isVerified]
	);

	const isVerifyButton = useMemo(() => [verifyButton], [verifyButton]);

	// Computed values
	const showVerifyButton =
		!directDepositDetails.isVerified &&
		directDepositDetails.bankType === 'FIRST_CAPITAL_BANK';
	const accountNumberColSpan = showVerifyButton ? 18 : 24;

	return (
		<div className={styles['directDepositForm']}>
			<div className={styles['directDepositForm-container']}>
				{isDetailsLoading ? (
					<Loader />
				) : (
					<Row
						className={styles['directDepositForm-container-card']}
						gutter={20}
					>
						<Col
							span={24}
							className={`${styles['col']} ${styles['input']} margin-top-10`}
						>
							<InputField
								name="accountHolderName"
								value={directDepositDetails.accountHolderName}
								label="Account Holder Name"
								required={true}
								helperText="Account Holder must contain letters and spaces"
								onChange={(value: any) =>
									handleChange(
										value,
										'accountHolderName',
										true
									)
								}
								isError={formError.accountHolderName}
							/>
						</Col>
						<Col
							span={24}
							className={`${styles['col']} ${styles['input']} margin-top-10`}
						>
							<SelectDropdown
								placeholder="Bank Name"
								options={BANK_NAME_OPTIONS}
								value={directDepositDetails.bankType}
								onChange={(value: any) =>
									handleChange(value, 'bankType', true)
								}
								size="large"
								required={true}
								helperText="Bank Name required"
								label="Bank Name"
								isError={formError.bankType}
							/>
						</Col>
						<Col
							span={accountNumberColSpan}
							className={styles['col']}
						>
							<InputField
								name="accountNumber"
								value={directDepositDetails.accountNumber}
								label="Account Number"
								required={true}
								helperText="Account Number required"
								onChange={(value) =>
									handleChange(value, 'accountNumber', true)
								}
								isError={formError.accountNumber}
							/>
						</Col>
						{showVerifyButton && (
							<Col span={6} className={styles['verfiyButton']}>
								<Buttons buttons={isVerifyButton} />
							</Col>
						)}

						<Col
							span={12}
							className={`${styles['col']} margin-top-10`}
						>
							<InputFieldWithZero
								name="sortCode"
								value={directDepositDetails.sortCode}
								label="Sort Code"
								required={true}
								helperText="Sort code must be a valid number"
								onChange={(value: any) =>
									handleChange(value, 'sortCode', true)
								}
								isError={formError.sortCode}
								disabled={sortCodeDisabled}
							/>
						</Col>
						<Col
							span={12}
							className={`${styles['col']} margin-top-10`}
						>
							<SelectDropdown
								placeholder="Select Beneficiary Bank Code"
								options={beneficiaryCodeDropdownOptions}
								value={directDepositDetails.beneficiaryBankCode}
								label="Beneficiary Bank Code"
								required={true}
								onChange={(value: any) =>
									handleChange(
										value,
										'beneficiaryBankCode',
										true
									)
								}
								size="large"
								helperText="Beneficiary Bank Code is required"
								isError={formError.beneficiaryBankCode}
								disabled={true} // Auto-filled based on bank selection
							/>
						</Col>
						<Col
							span={24}
							className={`${styles['col']} ${styles['input']} margin-top-10`}
						>
							<InputField
								name="beneficiaryAddr1"
								value={directDepositDetails.beneficiaryAddr1}
								label="Beneficiary Address 1"
								required={true}
								regex="^[a-zA-Z0-9 ]+$"
								helperText="Please enter valid address"
								onChange={(value: any) =>
									handleChange(
										value,
										'beneficiaryAddr1',
										true,
										ADDRESS_REGEX
									)
								}
								isError={formError.beneficiaryAddr1}
							/>
						</Col>
						<Col
							span={12}
							className={`${styles['col']} ${styles['input']} margin-top-10`}
						>
							<InputField
								name="beneficiaryAddr2"
								value={directDepositDetails.beneficiaryAddr2}
								label="Beneficiary Address 2"
								required={true}
								regex="^[a-zA-Z0-9 ]+$"
								helperText="Please enter valid address"
								onChange={(value: any) =>
									handleChange(
										value,
										'beneficiaryAddr2',
										true,
										ADDRESS_REGEX
									)
								}
								isError={formError.beneficiaryAddr2}
							/>
						</Col>
						<Col
							span={12}
							className={`${styles['col']} ${styles['input']} margin-top-10`}
						>
							<InputField
								name="beneficiaryAddr3"
								value={directDepositDetails.beneficiaryAddr3}
								label="Beneficiary Address 3"
								required={true}
								regex="^[a-zA-Z0-9 ]+$"
								helperText="Please enter valid address"
								onChange={(value: any) =>
									handleChange(
										value,
										'beneficiaryAddr3',
										true,
										ADDRESS_REGEX
									)
								}
								isError={formError.beneficiaryAddr3}
							/>
						</Col>
					</Row>
				)}

				<div className={styles['buttons']}>
					<Buttons buttons={myButtons} />
				</div>
			</div>
		</div>
	);
};

export default DirectDeposit;
